import { clamp } from "../utils";
import type { DetectedFace } from "./auto-reframe-engine";

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropOrigin {
  x: number;
  y: number;
}

/**
 * The framing knobs this module reads from `ReframeSettings`. `padding` is
 * deliberately absent: the crop is always the largest rectangle of the target
 * ratio that fits inside the source, so there is no slack for padding to spend.
 */
export interface CropFramingOptions {
  followSubject: boolean;
  centerBias: number;
  trackingSpeed: number;
}

export interface CropRequest {
  sourceWidth: number;
  sourceHeight: number;
  targetRatio: number;
  subject: DetectedFace | null;
  options: CropFramingOptions;
  /** Null on the first analysed frame. An origin of 0,0 is a real position. */
  previousCrop: CropOrigin | null;
}

// Anchoring the subject's centre above the crop's centre leaves headroom. A
// centred anchor crops the top of the head off on tall portrait ratios.
const SUBJECT_VERTICAL_ANCHOR = 0.35;

export function clamp01(value: number): number {
  return Number.isFinite(value) ? clamp(value, 0, 1) : 0;
}

export function toPixelSize(value: number): number {
  return Number.isFinite(value) ? Math.max(1, Math.round(value)) : 1;
}

export function fitCropToRatio(
  sourceWidth: number,
  sourceHeight: number,
  targetRatio: number,
): { width: number; height: number } {
  const safeWidth = toPixelSize(sourceWidth);
  const safeHeight = toPixelSize(sourceHeight);

  if (!Number.isFinite(targetRatio) || targetRatio <= 0) {
    return { width: safeWidth, height: safeHeight };
  }

  const sourceRatio = safeWidth / safeHeight;
  const width = sourceRatio > targetRatio ? safeHeight * targetRatio : safeWidth;
  const height =
    sourceRatio > targetRatio ? safeHeight : safeWidth / targetRatio;

  // Round to whole pixels here, before anything positions the crop. A crop
  // rounded up past the source edge cannot be brought back inside the frame by
  // clamping its origin, and would sample transparent pixels on the far edge.
  return {
    width: clamp(Math.round(width), 1, safeWidth),
    height: clamp(Math.round(height), 1, safeHeight),
  };
}

export function clampCropOrigin(
  x: number,
  y: number,
  cropWidth: number,
  cropHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): CropOrigin {
  const maxX = Math.max(0, toPixelSize(sourceWidth) - toPixelSize(cropWidth));
  const maxY = Math.max(0, toPixelSize(sourceHeight) - toPixelSize(cropHeight));

  return {
    x: Number.isFinite(x) ? clamp(x, 0, maxX) : 0,
    y: Number.isFinite(y) ? clamp(y, 0, maxY) : 0,
  };
}

export function computeCropRect(request: CropRequest): CropRect {
  const { sourceWidth, sourceHeight, targetRatio, subject, options } = request;
  const { width, height } = fitCropToRatio(
    sourceWidth,
    sourceHeight,
    targetRatio,
  );
  const safeWidth = toPixelSize(sourceWidth);
  const safeHeight = toPixelSize(sourceHeight);

  let targetX = (safeWidth - width) / 2;
  let targetY = (safeHeight - height) / 2;

  if (options.followSubject && subject) {
    const subjectCenterX = subject.x + subject.width / 2;
    const subjectCenterY = subject.y + subject.height / 2;

    targetX = subjectCenterX - width / 2;
    targetY = subjectCenterY - height * SUBJECT_VERTICAL_ANCHOR;

    const bias = clamp01(options.centerBias);
    targetX = targetX * (1 - bias) + ((safeWidth - width) / 2) * bias;
    targetY = targetY * (1 - bias) + ((safeHeight - height) / 2) * bias;
  }

  let origin = clampCropOrigin(
    targetX,
    targetY,
    width,
    height,
    safeWidth,
    safeHeight,
  );

  if (request.previousCrop) {
    const speed = clamp01(options.trackingSpeed);
    origin = {
      x: request.previousCrop.x + (origin.x - request.previousCrop.x) * speed,
      y: request.previousCrop.y + (origin.y - request.previousCrop.y) * speed,
    };
    // Clamp again: a previous origin carried over from a differently sized
    // source can pull the interpolated origin back outside the frame.
    origin = clampCropOrigin(
      origin.x,
      origin.y,
      width,
      height,
      safeWidth,
      safeHeight,
    );
  }

  return {
    x: Math.round(origin.x),
    y: Math.round(origin.y),
    width,
    height,
  };
}
